import axios, { type AxiosInstance } from "axios";
import {
  clearImpersonationSession,
  getActiveImpersonationSession,
} from "../auth/impersonationStore";

type TokenGetter = () => string | null;
type TokenRefresher = () => Promise<string | null>;
type BasicAuthHeaderGetter = () => string | null;
const API_BASE_URL_STORAGE_KEY = "hallticket.apiBaseURL";

interface HttpClientOptions {
  baseURL: string;
  getToken: TokenGetter;
  refreshToken?: TokenRefresher;
  getBasicAuthHeader?: BasicAuthHeaderGetter;
}

let tokenGetter: TokenGetter = () => null;
let tokenRefresher: TokenRefresher = async () => null;
let basicAuthHeaderGetter: BasicAuthHeaderGetter = () => null;
let refreshInFlight: Promise<string | null> | null = null;
let configuredBaseURL = "https://owl.hallticket.in";
const SKIP_AUTH_HEADER = "X-Skip-Auth";
const IMPERSONATION_HEADER = "X-Spot-Impersonation";
const COLLEGE_ADMIN_PATH_PREFIX = "/api/v1/spot/college-admin/";
const EXPORTER_PATH_PREFIX = "/admin/exporter/";
const ADMIN_USERNAME_STORAGE_KEY = "hallticket.admin.username.v1";

function isExporterRequestUrl(url: string | undefined): boolean {
  return typeof url === "string" && url.includes(EXPORTER_PATH_PREFIX);
}

function isCollegeAdminRequestUrl(url: string | undefined): boolean {
  return typeof url === "string" && url.includes(COLLEGE_ADMIN_PATH_PREFIX);
}

function normalizeBaseURL(rawValue: string): string | null {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsedUrl = new URL(trimmed);
    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return null;
    }
    return parsedUrl.href.replace(/\/$/, "");
  } catch {
    return null;
  }
}

function getLocalStorageBaseURL(): string | null {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return null;
  }

  const storedValue = window.localStorage.getItem(API_BASE_URL_STORAGE_KEY);
  if (!storedValue) {
    return null;
  }

  return normalizeBaseURL(storedValue);
}

function isDevEnvironment(): boolean {
  return process.env.NODE_ENV !== "production";
}

function readHeaderValue(headers: unknown, headerName: string): string | null {
  if (!headers) {
    return null;
  }

  const normalizedName = headerName.toLowerCase();
  if (typeof (headers as { get?: (name: string) => unknown }).get === "function") {
    const value = (headers as { get: (name: string) => unknown }).get(headerName);
    return typeof value === "string" && value.trim() ? value : null;
  }

  if (typeof headers !== "object") {
    return null;
  }

  const record = headers as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key.toLowerCase() === normalizedName && typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return null;
}

function deleteHeader(headers: unknown, headerName: string): void {
  if (!headers) {
    return;
  }

  if (typeof (headers as { delete?: (name: string) => void }).delete === "function") {
    (headers as { delete: (name: string) => void }).delete(headerName);
    return;
  }

  if (typeof headers === "object") {
    const record = headers as Record<string, unknown>;
    for (const key of Object.keys(record)) {
      if (key.toLowerCase() === headerName.toLowerCase()) {
        delete record[key];
      }
    }
  }
}

function setHeader(headers: Record<string, unknown>, headerName: string, value: string): void {
  if (typeof (headers as { set?: (name: string, value: string) => void }).set === "function") {
    (headers as { set: (name: string, value: string) => void }).set(headerName, value);
    return;
  }

  headers[headerName] = value;
}

function maskAuthorizationHeader(value: string | null): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.length <= 16) {
    return `${trimmed.slice(0, 4)}...${trimmed.slice(-2)}`;
  }

  return `${trimmed.slice(0, 10)}...${trimmed.slice(-4)}`;
}

function getAuthorizationScheme(value: string | null): "Basic" | "Bearer" | "None" | "Other" {
  if (!value) {
    return "None";
  }

  if (value.startsWith("Basic ")) {
    return "Basic";
  }

  if (value.startsWith("Bearer ")) {
    return "Bearer";
  }

  return "Other";
}

function getCurrentSessionUsername(): string | null {
  if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
    return null;
  }

  const username = window.sessionStorage.getItem(ADMIN_USERNAME_STORAGE_KEY);
  return username && username.trim() ? username.trim() : null;
}

export const httpClient: AxiosInstance = axios.create({
  baseURL: configuredBaseURL,
  withCredentials: false,
});

httpClient.interceptors.request.use((config) => {
  const localStorageBaseURL = getLocalStorageBaseURL();
  config.baseURL = localStorageBaseURL ?? configuredBaseURL;
  config.withCredentials = config.withCredentials ?? false;

  const headers = config.headers;
  const skipAuthHeaderValue =
    typeof (headers as { get?: (name: string) => unknown })?.get === "function"
      ? (headers as { get: (name: string) => unknown }).get(SKIP_AUTH_HEADER)
      : (headers as Record<string, unknown> | undefined)?.[SKIP_AUTH_HEADER];
  const shouldSkipAuth = skipAuthHeaderValue === "1" || skipAuthHeaderValue === "true";

  if (headers) {
    deleteHeader(headers, SKIP_AUTH_HEADER);
  }

  const impersonationSession = getActiveImpersonationSession();
  const useImpersonation =
    Boolean(impersonationSession) && isCollegeAdminRequestUrl(config.url);

  if (useImpersonation && impersonationSession) {
    config.headers = headers ?? {};
    const normalizedHeaders = config.headers as Record<string, unknown>;

    // College Admin APIs while impersonating: impersonation token only — no Authorization.
    deleteHeader(normalizedHeaders, "Authorization");
    setHeader(normalizedHeaders, IMPERSONATION_HEADER, impersonationSession.token);

    const authAfter = readHeaderValue(normalizedHeaders, "Authorization");
    console.info("[impersonation] Request using X-Spot-Impersonation", {
      url: config.url,
      method: (config.method ?? "GET").toUpperCase(),
      collegeId: impersonationSession.collegeId,
      authorizationOmitted: authAfter == null,
      impersonationHeader: IMPERSONATION_HEADER,
      tokenMasked: maskAuthorizationHeader(impersonationSession.token),
    });

    return config;
  }

  // Non–college-admin (or not impersonating): never send the impersonation header.
  if (headers) {
    deleteHeader(headers, IMPERSONATION_HEADER);
  }

  const token = tokenGetter();
  const basicAuthHeader = basicAuthHeaderGetter();

  if (!shouldSkipAuth) {
    config.headers = headers ?? {};
    const normalizedHeaders = config.headers;
    const existingAuth =
      typeof (normalizedHeaders as { get?: (name: string) => unknown }).get === "function"
        ? (normalizedHeaders as { get: (name: string) => unknown }).get("Authorization")
        : (normalizedHeaders as { Authorization?: unknown }).Authorization;
    if (!existingAuth) {
      if (basicAuthHeader) {
        (normalizedHeaders as { Authorization: string }).Authorization = basicAuthHeader;
      } else if (token) {
        const bearerHeader = `Bearer ${token}`;
        (normalizedHeaders as { Authorization: string }).Authorization = bearerHeader;
      }
    }
  }

  if (isDevEnvironment() && isExporterRequestUrl(config.url)) {
    const authHeader = readHeaderValue(config.headers, "Authorization");
    const acceptHeader = readHeaderValue(config.headers, "Accept");
    const contentTypeHeader = readHeaderValue(config.headers, "Content-Type");
    console.info("[http-client] Final exporter request headers", {
      url: config.url,
      method: (config.method ?? "GET").toUpperCase(),
      authorizationScheme: getAuthorizationScheme(authHeader),
      authorizationMasked: maskAuthorizationHeader(authHeader),
      username: getCurrentSessionUsername(),
      accept: acceptHeader,
      contentType: contentTypeHeader,
      withCredentials: config.withCredentials ?? false,
    });
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error) || !error.config) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const requestUrl = error.config.url;
    const wasCollegeAdminImpersonationRequest =
      isCollegeAdminRequestUrl(requestUrl)
      && Boolean(readHeaderValue(error.config.headers, IMPERSONATION_HEADER));

    // Impersonation token rejected/expired → clear and return to Super Admin auth mode.
    if (
      wasCollegeAdminImpersonationRequest
      && (status === 401 || status === 403)
    ) {
      console.info("[impersonation] Received auth failure — clearing impersonation state", {
        url: requestUrl,
        status,
      });
      clearImpersonationSession();
      return Promise.reject(error);
    }

    if (status !== 401) {
      return Promise.reject(error);
    }

    const requestConfig = error.config as typeof error.config & { _retry?: boolean };
    if (isExporterRequestUrl(requestConfig.url)) {
      return Promise.reject(error);
    }

    // Do not attempt Bearer refresh for college-admin impersonation traffic.
    if (isCollegeAdminRequestUrl(requestConfig.url)) {
      return Promise.reject(error);
    }

    if (requestConfig._retry) {
      return Promise.reject(error);
    }

    requestConfig._retry = true;

    if (!refreshInFlight) {
      refreshInFlight = tokenRefresher().finally(() => {
        refreshInFlight = null;
      });
    }

    const refreshedToken = await refreshInFlight;
    if (!refreshedToken) {
      return Promise.reject(error);
    }

    requestConfig.headers = requestConfig.headers ?? {};
    requestConfig.headers.Authorization = `Bearer ${refreshedToken}`;
    return httpClient(requestConfig);
  }
);

export function initializeHttpClient(options: HttpClientOptions): AxiosInstance {
  configuredBaseURL = normalizeBaseURL(options.baseURL) ?? "https://owl.hallticket.in";
  httpClient.defaults.baseURL = getLocalStorageBaseURL() ?? configuredBaseURL;
  httpClient.defaults.withCredentials = false;
  tokenGetter = options.getToken;
  tokenRefresher = options.refreshToken ?? (async () => null);
  basicAuthHeaderGetter = options.getBasicAuthHeader ?? (() => null);
  return httpClient;
}
